import { FirebaseRepository } from '@breakoutlearning/firebase-repository/models/FirebaseRepository'
import type { IReactionDisposer } from 'mobx'
import { reaction } from 'mobx'
import { useEffect, useMemo, useRef, useState } from 'react'
import { getFirebaseConfig } from '../../config/firebase'
import { AuthenticationContext } from '../../hooks/auth'
import { inBeta, inE2E, environment } from 'config/environment'
import { getAppCommitSha } from 'util/commitSha'
import { areWeInIframe } from 'util/areWeInIframe'
import { getNavigationType } from 'util/navigationType'
import { setRepository } from 'config/sentry'
import { repositoryApi } from 'config/api'
import { getSlideDeckReadSource } from 'util/slideDeckSource'
import { ErrorBoundary } from 'react-error-boundary'
import { TopLevelErrorFallback } from './TopLevelErrorFallback'

export const AuthenticationContextProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const repository = useMemo(() => {
    const repo = FirebaseRepository.build({
      firebase: getFirebaseConfig(),
      useLocalPersistence: inE2E(),
      disableAnalytics: inE2E(),
      inBeta: inBeta(),
      environment: environment(),
      api: repositoryApi,
      // Per-tab v1/v2 slide-deck read override (sessionStorage). Read once at
      // build; the toggle reloads the page, so a fresh repository re-reads it.
      slideDeckReadSource: getSlideDeckReadSource(),
    }).initialize()
    setRepository(repo)
    return repo
  }, [])

  const [authenticated, setAuthenticated] = useState(repository.isAuthenticated)
  const [initialized, setInitialized] = useState(repository.initialized)

  const usingLocalEmulator = __USE_EMULATOR__

  useEffect(() => {
    if (usingLocalEmulator) {
      const host = import.meta.env.VITE_USE_EMULATOR_HOST || 'localhost'
      try {
        repository.connectToEmulator(host)
      } catch (e) {
        console.error(e)
      }
    }
  }, [repository, usingLocalEmulator])

  useEffect(() => {
    const disposers: IReactionDisposer[] = []
    disposers.push(
      reaction(
        () => repository.isAuthenticated,
        (isAuthenticated) => {
          setAuthenticated(isAuthenticated)
        },
        { fireImmediately: true }
      )
    )
    // only add this reaction if the component is not initialized
    if (!initialized) {
      disposers.push(
        reaction(
          () => repository.initialized,
          (isInitialized) => {
            setInitialized(isInitialized)
          },
          { fireImmediately: true }
        )
      )
    }
    return () => {
      disposers.forEach((disposer) => disposer())
    }
  }, [repository, initialized])

  // Boot event: one per full page load. Gated on `initialized` (the same
  // auth-ready signal Entrypoint waits on) so `authenticated` is the settled
  // value — firing on mount would report every logged-in user as logged-out.
  // The ref keeps it to a single emission despite `authenticated` being a dep.
  const appOpenLoggedRef = useRef(false)
  useEffect(() => {
    if (appOpenLoggedRef.current || !initialized) return
    appOpenLoggedRef.current = true

    repository.logEvent('app_open', {
      app_version: getAppCommitSha(),
      navigation_type: getNavigationType(),
      authenticated,
      in_iframe: areWeInIframe(),
      page_location: window.location.pathname,
      visibility_state: document.visibilityState,
      ...(document.referrer && { referrer: document.referrer }),
    })
  }, [repository, initialized, authenticated])

  // Log foreground/background transitions. `visibilitychange` fires while the
  // page is still alive (unlike unload), and GA flushes its queue on hide via
  // sendBeacon, so the "hidden" event is usually delivered. This is how we
  // confirm a tab's activity bursts line up with it being foregrounded.
  useEffect(() => {
    const onVisibilityChange = () => {
      repository.logEvent('visibility_change', {
        visibility_state: document.visibilityState,
      })
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () =>
      document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [repository])

  return (
    <ErrorBoundary FallbackComponent={TopLevelErrorFallback}>
      <AuthenticationContext.Provider
        value={{
          initialized,
          repository,
          authenticated,
          usingLocalEmulator,
          setAuthenticated,
        }}
      >
        {children}
      </AuthenticationContext.Provider>
    </ErrorBoundary>
  )
}
